import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CaseDetailsWithEmailResult, EmployeeCaseOption, StiiraError } from '@core/models';
import { CaseCalendarDay, PostIntermittentTime, PostIntermittentTimeForm, SubmitIntermittentTimeDialog, TimeSubmittal, TimeSubmittalForm } from '@core/models/leave-admin/submit-intermittent-time-dialog.model';
import { ErrorService, LayoutService, NotificationService, SnackbarService } from '@core/services';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { parseDateHours } from '@shared/helpers/data-parsers.helpers';
import { DatePipe } from '@angular/common';
import { EmployeeLeaveHoursExtended } from '@core/models/leave-admin/leave-calendar/leave-calendar.model';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { EmailResult } from '@core/enums';

@Component({
    selector: 'app-submit-intermittent-time',
    templateUrl: './submit-intermittent-time.component.html',
    styleUrls: ['./submit-intermittent-time.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SubmitIntermittentTimeComponent implements OnInit {
  static readonly componentName = 'SubmitIntermittentTimeComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  @ViewChild('addTimeButton', { read: ElementRef }) private addTimeButton: ElementRef<HTMLButtonElement>;

  public form: FormGroup<PostIntermittentTimeForm>;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public isLoadingEmployee$: Subject<boolean> = new Subject<boolean>();
  public filteredOptions: Observable<EmployeeCaseOption[]>;
  public hoursTakenWarning: number = null;
  public disableClipboardButton: boolean = false;
  public hoursExtended: EmployeeLeaveHoursExtended[] = null;
  public existingTimeSubmittals: TimeSubmittal[] = null;
  public showNotificationToggle: boolean;
  public caseCalendar: CaseCalendarDay[] = null;
  public allBanksFullDayIncrementSub = new BehaviorSubject<boolean>(false);
  public someBanksFullDayIncrementSub = new BehaviorSubject<boolean>(false);
  
  public position = position;
  public onDragEnded = onDragEnded;
  
  private $someBanksFullDayIncrement = this.someBanksFullDayIncrementSub.asObservable();
  private formChangeEmitted: boolean = false;
  private formInitValues: any;
  private showDateMatchHint: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value) === JSON.stringify(this.formInitValues);
  }

  public isMobile$: Observable<boolean>;

  get employee(): FormControl<EmployeeCaseOption> {
    return this.form.controls.caseId as FormControl<EmployeeCaseOption>;
  }  

  get timeSubmittals(): FormArray<FormGroup<TimeSubmittalForm>> {
    return this.form.controls.timeSubmittals as FormArray<FormGroup<TimeSubmittalForm>>;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { 
      submitIntermittentTimeDialog: SubmitIntermittentTimeDialog,
      showSubmitTime: boolean,
      sysText: any,
      unsavedChangesSystext: any
    },

    private fb: FormBuilder,
    private dialogRef: MatDialogRef<SubmitIntermittentTimeComponent>,
    private dialog: MatDialog,
    private layoutService: LayoutService,
    private errorService: ErrorService,
    private service: LeaveAdminService,
    private store: LeaveAdminStoreService,
    private notifyService: NotificationService,
    private datePipe: DatePipe,
    private snackbarService: SnackbarService,
    private notificationService: NotificationService
  ) { 
    this.form = this.fb.group<PostIntermittentTimeForm>({
      caseId: this.fb.control(null, Validators.required),
      timeSubmittals: new FormArray<FormGroup<TimeSubmittalForm>>([]),
      sendNotification: this.fb.control(false)
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close(false));

    if (!this.data.submitIntermittentTimeDialog.showLeavePickList) {
      this.employee.patchValue(this.data.submitIntermittentTimeDialog?.employeeCaseOptions[0]);
    }

    this.setDialogValues(this.data.submitIntermittentTimeDialog);
    this.addTimeSubmittalFormGroup();

    // when the selected employee changes, get the extended 
    // hours for the new employee to support the check for 
    // other leaves on the same date 
    this.employee.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        if (val?.caseId != null) {
          this.form.disable({ emitEvent: false });
          // the picker stays enabled (readonly while loading), so focus stays in it
          this.employee.enable({ emitEvent: false });
          this.service.getSubmitIntermittentTimeDialog(val.caseId)
            .pipe(indicate(this.isLoadingEmployee$))
            .subscribe(
              (res) => { 
                this.form.enable({ emitEvent: false });
                this.setDialogValues(res);
                this.formValidation();
              },
              (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors)
            )
        }
        else {
          this.setDialogValues(null);
          this.formValidation();
        }
      });

    // clipboard readText() is currently not supported 
    // by Firefox, disable button if not supported 
    if (!navigator['clipboard'] || !(typeof navigator['clipboard'].readText === "function")) {
      this.disableClipboardButton = true;
    }

    setTimeout(() => {
      this.formInitValues = { ...this.form.value }
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public addTimeSubmittalFormGroup() {
    const timeSubmittalGroup = this.fb.group<TimeSubmittalForm>({
      submittedDate: this.fb.control(null, Validators.required),
      totalTimeSubmitted: this.fb.control(null, [Validators.required, Validators.pattern(/^\s*(?=.*[0-9])\d*(?:\.\d{1,2})?\s*$/)]),
      isFullDay: this.fb.control(null),
      fullDayIncrement: this.fb.control(null),
      notFullDayWarn: this.fb.control(null),
      timeSubmittalWarn: this.fb.control(null),
      sameDateWarn: this.fb.control(null),
      otherLeavesWarn: this.fb.control(null)
    });

    // set up the follwoing subscriptions (note they only apply if some banks 
    // restict to full day inrements but set them up now since case can change):
    //  - on date selection grab the workday hours from the case calendar
    //  - on hours field, if hours equals workday hours check full day box
    //  - on full day checkbox, if checked set horus to full workday value
    timeSubmittalGroup.controls.submittedDate.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.handleFullDayDateSelection(timeSubmittalGroup);
      });
    timeSubmittalGroup.controls.submittedDate.setValue(null); // trigger inital change
    timeSubmittalGroup.controls.totalTimeSubmitted.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.handleFullDayHoursChange(timeSubmittalGroup);
      });
    timeSubmittalGroup.controls.isFullDay.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=>{
        this.handleFullDayChange(timeSubmittalGroup);
      });

    // if any banks restrict to full work days disable full day checkbox
    // until a date is selected (to determine if that date is a workday and if so 
    // how many workday hours it is) 
    if (this.someBanksFullDayIncrementSub.value) {
      timeSubmittalGroup.controls.isFullDay.disable();
    }

    // if employee picklist is available set up additional subscriptions
    if (this.data.submitIntermittentTimeDialog.showLeavePickList) {
      // ...if adding a group and no ee selected disable group
      if (!this.employee.value) {
        timeSubmittalGroup.disable({emitEvent: false});
      }

      // ...listen to changes to someBanksFullDayIncrement (based off ee selection)
      this.$someBanksFullDayIncrement
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (res) {
            // if some banks restrict run date selection for existing dates
            this.handleFullDayDateSelection(timeSubmittalGroup);
            this.handleFullDayHoursChange(timeSubmittalGroup);
            // if no date selected disable checkbox
            if (timeSubmittalGroup.controls.isFullDay.enabled
            && !timeSubmittalGroup.controls.submittedDate.value) {
              timeSubmittalGroup.controls.isFullDay.disable({emitEvent: false});
            }
          }
        });

      // ...if ee value cleared disable group
      this.employee.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          if (!res) {
            timeSubmittalGroup.disable();
          }
        });
    }

    // finally set up group validation subscription
    timeSubmittalGroup.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formValidation();
      });
  
    this.timeSubmittals.push(timeSubmittalGroup)
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  public onSubmit(): void {
    this.form.markAsUntouched();
    this.hoursTakenWarning = null;

    if (!this.data.submitIntermittentTimeDialog.showLeavePickList && this.store.isActionRunning) {
      this.openPendingChangesDialog();
      return;
    }

    const dto: PostIntermittentTime = {
      caseId: this.employee?.value?.caseId,
      timeSubmittals: this.timeSubmittals.getRawValue().map(v => {
        const ts: TimeSubmittal = {
          submittedDate: v.submittedDate,
          totalTimeSubmitted: v.totalTimeSubmitted
        };
        return ts;
      }),
      sendNotification: this.showNotificationToggle
        ? this.form.controls.sendNotification.value
        : null
    };

    this.service.postIntermittentTime(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res) => {
        this.handleSuccessMessage(res);
        this.dialogRef.close(res);
    },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
  }

  public onRemoveTimeSubmittal(index: number): void {
    // the pressed button goes with its row
    this.addTimeButton.nativeElement.focus();
    this.timeSubmittals.removeAt(index);
  }

  public onAddTimeSubmittal() {
    this.addTimeSubmittalFormGroup();
  }

  public onPasteIntoField(event: ClipboardEvent, index: number): void {
    if (event.type === "paste"){
      this.handleHoursPaste(event.clipboardData.getData("text"), index, true);
    }
  }

  public onAddFromClipboard(): void {
    const formGroup = this.timeSubmittals.controls[0];
    const firstRowEmpty = !formGroup.value.submittedDate && !formGroup.value.totalTimeSubmitted;
    const startingIndex = (firstRowEmpty 
      && this.timeSubmittals.controls.length == 1) ? 0 : this.timeSubmittals.controls.length;
    navigator['clipboard'].readText()
      .then(clipText => {
        this.handleHoursPaste(clipText, startingIndex);
      })
      .catch(() => {
        this.notifyService.showErrorToast(this.data.sysText.clipboardError);
      });
  }

  private formValidation(): void {
    this.showDateMatchHint = false;
    this.timeSubmittals.controls.forEach((formGroup, i) => {
      if (this.employee.value) {
        const submittedDate = new Date(formGroup.value.submittedDate?.toDateString());
        const hoursTaken = formGroup.value.totalTimeSubmitted;
        this.checkDateFieldMatch(submittedDate, formGroup, this.timeSubmittals, i);
        this.checkDateRange(submittedDate, formGroup);
        this.checkDateHistory(submittedDate, formGroup);
        this.checkHours(hoursTaken, formGroup);
        this.checkOtherLeaves(submittedDate, formGroup);
      }
    });
  }

  private checkHours(hoursTaken: number, formGroup: FormGroup<TimeSubmittalForm>): void {
    if (hoursTaken > 24) {
      formGroup.controls.totalTimeSubmitted.setErrors({ 'hoursGreaterThan24': true });
    } else {
      // re-run the field's own validators (required, pattern) instead of clearing their errors
      formGroup.controls.totalTimeSubmitted.updateValueAndValidity({ emitEvent: false });
    }
  }

  private checkDateRange(submittedDate: Date, formGroup: FormGroup<TimeSubmittalForm>): void {
    const leaveInfo = this.employee.value;
    const leaveEndDate = new Date(leaveInfo.endDate);
    const leaveStartDate = new Date(leaveInfo.startDate);
    
    if ((submittedDate < leaveStartDate || submittedDate > leaveEndDate)) {
      formGroup.controls.submittedDate.setErrors({ 'dateRangeInvalid': true });
    } else {
      // re-run the field's own validators (required, unreadable date) instead of clearing their errors
      formGroup.controls.submittedDate.updateValueAndValidity({ emitEvent: false });
    }
  }

  private checkDateFieldMatch(
    submittedDate: Date, 
    formGroup: FormGroup<TimeSubmittalForm>, 
    formArray: FormArray<FormGroup<TimeSubmittalForm>>, 
    fieldIndex: number): void {
    let hasMatch = false;

    for (let i = 0; i < formArray.controls.length; i++) {
      if (fieldIndex != i) {
        const otherFormGroup = formArray.controls[i];
        const othersubmittedDate = new Date(otherFormGroup.value.submittedDate?.toDateString());
        if (submittedDate.getTime() === othersubmittedDate.getTime()) {
          let controlVal = this.showDateMatchHint ? formGroup.value.submittedDate : null;

          if (formGroup.controls.sameDateWarn.value != controlVal) {
            formGroup.controls.sameDateWarn.setValue(controlVal);
          }

          this.showDateMatchHint = true;
          hasMatch = true;
          break;
        }
      }
    }
    
    if (!hasMatch && formGroup.controls.sameDateWarn.value != null) {
      formGroup.controls.sameDateWarn.setValue(null);
    }
  }

  private checkDateHistory(submittedDate: Date, formGroup: FormGroup<TimeSubmittalForm>): void {
    let foundDup = false;
    
    if (this.existingTimeSubmittals) {
      for (let i = 0; i < this.existingTimeSubmittals.length; i++) {
        let submittedDateRecorded = new Date(this.existingTimeSubmittals[i].submittedDate);

        if (submittedDate.getTime() === submittedDateRecorded.getTime()) {
          if (formGroup.controls.timeSubmittalWarn.value != this.existingTimeSubmittals[i].totalTimeSubmitted) {
            formGroup.controls.timeSubmittalWarn.setValue(this.existingTimeSubmittals[i].totalTimeSubmitted);
          }

          foundDup = true;
          break;
        }
      }

      if (!foundDup && formGroup.controls.timeSubmittalWarn.value != null) {
        formGroup.controls.timeSubmittalWarn.setValue(null);
      }
    }
  }

  private checkOtherLeaves(submittedDate: Date, formGroup: FormGroup<TimeSubmittalForm>): void {
    if (isNaN(submittedDate?.getTime()) || this.hoursExtended == null) {
      if (formGroup.controls.otherLeavesWarn.value != null) {
        formGroup.controls.otherLeavesWarn.setValue(null);
      }
      return;
    }

    let subDateYmd = this.datePipe.transform(new Date(submittedDate), 'yyyy-MM-dd');
    let hoursExtended = this.hoursExtended.find(he => 
      this.datePipe.transform(he.calendarDate, 'yyyy-MM-dd') == subDateYmd);

    if (hoursExtended != null
      && ((hoursExtended.leaveCaseHoursID == null && hoursExtended.leavesCount > 0)
        || (hoursExtended.leaveCaseHoursID != null && hoursExtended.leavesCount > 1))) {
      if (formGroup.controls.otherLeavesWarn.value != true) {
        formGroup.controls.otherLeavesWarn.setValue(true);
      }
    }
    else if (formGroup.controls.otherLeavesWarn.value != null) {
      formGroup.controls.otherLeavesWarn.setValue(null);
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSystext,
    };

    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private openPendingChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px',
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.onSubmit();
      }
    });
  }

  private handleHoursPaste(text: string, startingIndex: number, pasteIntoRow: boolean = false): void {
    if (pasteIntoRow) {
      for (let i = (this.timeSubmittals.controls.length - 1); i > startingIndex; i--) {
        this.onRemoveTimeSubmittal(i);
      }
    }

    let dontAddFormGroupRow = startingIndex == 0 || pasteIntoRow;
    const dateHoursArray = parseDateHours(text);
    dateHoursArray.forEach((dh, i) => {
      if (!dontAddFormGroupRow) {
        this.addTimeSubmittalFormGroup();
      }
      dontAddFormGroupRow = false;
      setTimeout(() => {
        const formGroup = this.timeSubmittals.at(i + startingIndex);
        formGroup.patchValue({
          submittedDate: dh.date,
          totalTimeSubmitted: +dh.hours
        });
        formGroup.markAllAsTouched();
      }, 0);
    })
  }

  private handleSuccessMessage(res: CaseDetailsWithEmailResult): void {
    if (res?.emailResult == EmailResult.success) {
      this.snackbarService.open(this.data.sysText.submitIntermittentTimeWithEmailSuccess, this.data.sysText.dismiss);
    }
    else if (res?.emailResult == EmailResult.successWithFailure) {
      this.notificationService.showErrorToast(this.data.sysText.submitIntermittentTimeWithEmailSuccessFailure);
    }
    else if (res?.emailResult == EmailResult.failure) {
      this.notificationService.showErrorToast(this.data.sysText.submitIntermittentTimeWithEmailFailure);
    }
    else if (res?.emailResult == EmailResult.doesntApply) {
      this.snackbarService.open(this.data.sysText.submitIntermittentTimeWithoutEmail, this.data.sysText.dismiss);
    }
  }

  private handleFullDayDateSelection(formGroup: FormGroup<TimeSubmittalForm>): void {
    if (!this.someBanksFullDayIncrementSub.value) {
      return;
    }
    const selectedDate = this.datePipe.transform(new Date(formGroup.controls.submittedDate.value), 'yyyy-MM-dd');
    const caseCalDay = this.caseCalendar?.find(cd => 
      this.datePipe.transform(new Date(cd.date), 'yyyy-MM-dd') == selectedDate);

    if (selectedDate && caseCalDay?.fullDayIncrement) {
      formGroup.controls.fullDayIncrement.setValue(caseCalDay?.fullDayIncrement), {emitEvent: false};
      formGroup.controls.isFullDay.enable({emitEvent: false});
    } else {
      formGroup.controls.isFullDay.setValue(false, {emitEvent: false});
      formGroup.controls.fullDayIncrement.setValue(null);
      formGroup.controls.isFullDay.disable({emitEvent: false});
    }

    // auto patch workday value on check when all banks restrict
    // to full day increments
    if (this.allBanksFullDayIncrementSub.value
      && caseCalDay?.fullDayIncrement) {
      formGroup.patchValue({
        totalTimeSubmitted: caseCalDay?.fullDayIncrement,
        isFullDay: true
      });
    }
    this.handleFullDayHoursChange(formGroup);
  }

  private handleFullDayHoursChange(formGroup: FormGroup<TimeSubmittalForm>): void {
    if (!this.someBanksFullDayIncrementSub.value) {
      return;
    }
    const selectedDate = this.datePipe.transform(new Date(formGroup.controls.submittedDate.value), 'yyyy-MM-dd');
    const caseCalDay = this.caseCalendar?.find(cd => 
      this.datePipe.transform(new Date(cd.date), 'yyyy-MM-dd') == selectedDate);
    const hours = formGroup.controls.totalTimeSubmitted.value;

    // handle setting full day checkbox if hours equal full day workhours
    if (!!hours && hours == caseCalDay?.fullDayIncrement) {
      formGroup.controls.isFullDay.setValue(true, {emitEvent: false});
    } else if (formGroup.controls.isFullDay.value) {
      formGroup.controls.isFullDay.setValue(false, {emitEvent: false});
    }
    
    // handle 'not full day' warning
    if (this.allBanksFullDayIncrementSub.value
      && !!formGroup.controls.submittedDate.value
      && !!formGroup.controls.totalTimeSubmitted.value
      && (!caseCalDay?.fullDayIncrement 
      || formGroup.controls.totalTimeSubmitted.value != caseCalDay?.fullDayIncrement)) {
      formGroup.controls.notFullDayWarn.setValue(true);
    } else {
      formGroup.controls.notFullDayWarn.setValue(false);
    }
  }

  private handleFullDayChange(formGroup: FormGroup<TimeSubmittalForm>): void {
    if (!this.someBanksFullDayIncrementSub.value) {
      return;
    }
    const selectedDate = this.datePipe.transform(new Date(formGroup.controls.submittedDate.value), 'yyyy-MM-dd');
    const caseCalDay = this.caseCalendar?.find(cd => 
      this.datePipe.transform(new Date(cd.date), 'yyyy-MM-dd') == selectedDate);
    const isFullDay = formGroup.controls.isFullDay.value;
    if (isFullDay && caseCalDay?.fullDayIncrement) {
      formGroup.controls.totalTimeSubmitted.setValue(caseCalDay?.fullDayIncrement);
    } else if (!isFullDay 
      && !!caseCalDay?.fullDayIncrement
      && caseCalDay?.fullDayIncrement == formGroup.controls.totalTimeSubmitted.value) {
      formGroup.controls.isFullDay.setValue(true, {emitEvent: false});
    }
  }

  private setDialogValues(dialog: SubmitIntermittentTimeDialog): void {
    this.hoursExtended = dialog?.hoursExtended ?? null;
    this.existingTimeSubmittals = dialog?.timeSubmittals?? null;
    this.caseCalendar = dialog?.caseCalendar?? null;
    this.showNotificationToggle = dialog?.showNotificationToggle ?? false;
    this.someBanksFullDayIncrementSub.next(dialog?.someBanksFullDayIncrement ?? false);
    this.allBanksFullDayIncrementSub.next(dialog?.allBanksFullDayIncrement ?? false);
  }
}